import React, { useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const EXP_SAMPLE = JSON.stringify([
  { WorkEmail: 'jane.doe@tricorps.com', EmployeeNumber: '1001', Amount: 42.5, SubmittedDate: '2026-11-01', MerchantName: 'Uber', Category: 'Travel' },
  { WorkEmail: 'unknown@example.com', Amount: 19.0, SubmittedDate: '2026-11-02', MerchantName: 'Office Depot', Category: 'Supplies' },
], null, 2);
const WT_SAMPLE = JSON.stringify([{ EmployeeNumber: '1001', EmployeeName: 'Sample One', CheckDate: '2026-11-30', CheckNumber: '555', Amount: 2450.12, TotalWages: 3000, DetailTaxDescription: 'Federal Tax', DetailTaxAmount: 330 }], null, 2);

export const ImportsSection = () => {
  const [exp, setExp] = useState(EXP_SAMPLE);
  const [expRes, setExpRes] = useState(null);
  const [wt, setWt] = useState(WT_SAMPLE);
  const [wtDate, setWtDate] = useState('2026-11-30');
  const [wtRes, setWtRes] = useState(null);

  const parse = (s) => { try { return JSON.parse(s); } catch { toast.error('Rows must be a valid JSON array'); return null; } };

  const runExp = async () => {
    const rows = parse(exp); if (!rows) return;
    try { const r = await payrollAPI.expensifyImport({ rows }); setExpRes(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
  };

  const runWt = async () => {
    const rows = parse(wt); if (!rows) return;
    try { const r = await payrollAPI.wtImport({ rows, check_date: wtDate }); setWtRes(r.data); if (r.data.ok) toast.success(`Imported ${r.data.checks} check(s) as ${r.data.version}`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
  };

  const finalizeWt = async () => {
    try { await payrollAPI.wtFinalize(wtRes.import_id); toast.success('Import finalized'); setWtRes({ ...wtRes, status: 'final' }); }
    catch (e) { toast.error('Finalize failed'); }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="netpay-expensify">
        <CardHeader><CardTitle>Reimbursements — Expensify import</CardTitle>
          <CardDescription>Each row is matched to an employee by work email first, then by employee number. Matched rows are saved as reimbursements on that employee; any row that matches no employee is sent to the review list below (never guessed, never silently dropped).</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Textarea data-testid="exp-rows" rows={6} value={exp} onChange={(e) => setExp(e.target.value)} />
          <Button data-testid="exp-run" onClick={runExp}>Import Expensify</Button>
          {expRes && (
            <div className="text-sm border rounded p-3 space-y-2" data-testid="exp-result">
              <div>Matched &amp; saved: <b data-testid="exp-matched">{expRes.matched ?? expRes.saved ?? 0}</b> · To review: <b data-testid="exp-review-count">{(expRes.needs_review || []).length}</b> · Errors: {expRes.errors?.length || 0} · Unmapped: {(expRes.unmapped_columns || []).join(', ') || 'none'}</div>
              {(expRes.needs_review || []).length > 0 && (
                <div className="border rounded overflow-auto max-h-56" data-testid="exp-review-table">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500"><th className="p-2">Row</th><th>Email</th><th>Emp #</th><th>Amount</th><th>Date</th><th>Why</th></tr></thead>
                    <tbody>
                      {expRes.needs_review.map((n, i) => (
                        <tr key={i} className="border-t text-amber-700" data-testid={`exp-review-${i}`}>
                          <td className="p-2">{n.row}</td><td>{n.email || '—'}</td><td>{n.employee_number || '—'}</td>
                          <td>{n.amount}</td><td>{n.date || '—'}</td><td>{n.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(expRes.errors || []).map((er, i) => (<div key={i} className="text-red-700" data-testid={`exp-error-${i}`}>Row {er.row}: {er.error}</div>))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="netpay-winteam-import">
        <CardHeader><CardTitle>WinTeam Payroll Check History import</CardTitle>
          <CardDescription>Imports the WinTeam PAY Report for one check date, mapped by header name, grouped per employee + check. Versioned Draft → Final. SocialSecurityNumber is never imported. Paste the exported rows (header-keyed JSON).</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <label className="text-sm">Check date<Input data-testid="wt-date" value={wtDate} onChange={(e) => setWtDate(e.target.value)} className="w-40" /></label>
          <Textarea data-testid="wt-rows" rows={5} value={wt} onChange={(e) => setWt(e.target.value)} />
          <div className="flex gap-2">
            <Button data-testid="wt-run" onClick={runWt}>Import check history</Button>
            {wtRes?.import_id && wtRes.status !== 'final' && <Button variant="outline" data-testid="wt-finalize" onClick={finalizeWt}>Finalize {wtRes.version}</Button>}
          </div>
          {wtRes && (
            <div className="text-sm border rounded p-3 space-y-1" data-testid="wt-result">
              <div>Version <b>{wtRes.version}</b> {wtRes.status === 'final' && <Badge>final</Badge>} · Checks: {wtRes.checks} · Unmapped: {(wtRes.unmapped_columns || []).join(', ') || 'none'}</div>
              {wtRes.note && <div className="text-xs text-slate-500">{wtRes.note}</div>}
              {(wtRes.errors || []).length > 0 && <div className="text-amber-700">Row errors: {wtRes.errors.length}</div>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
